/**
 * useMarketBI — Market Intelligence BI Computation Hook
 *
 * Centralises heavy BI calculations (brand summaries, daily velocity,
 * projections, leaderboards) via useMemo so they run efficiently.
 */

import { useMemo } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MapLocationBI {
  id: string;
  name: string;
  brand_id: string;
  brand_name?: string;
  brand_logo_url?: string;
  current_sales?: number;
  monthly_target?: number;
  effective_monthly_target?: number;
  daily_target?: number;
  effective_daily_target?: number;
}

export interface TargetEntry {
  monthly: string;
  daily: string;
  calcRule: string;
}

export interface DailyHistoryEntry {
  location_id: string;
  entry_date: string;
  sales_amount: number;
}

export interface BrandSummary {
  brandId: string;
  brandName: string;
  brandLogo: string;
  branchCount: number;
  totalTarget: number;
  totalActual: number;
  achievementPct: number;
  gap: number;
}

export interface BranchPerformance {
  id: string;
  name: string;
  brandName: string;
  target: number;
  actual: number;
  achievementPct: number;
  gap: number;
}

export interface DailyVelocityPoint {
  day: number;
  date: string;
  totalSales: number;
  idealCumulative: number;
  actualCumulative: number;
}

export interface ProjectionResult {
  totalTarget: number;
  totalActual: number;
  projectedTotal: number;
  projectedPct: number;
  projectedGap: number;
  daysElapsed: number;
  daysInMonth: number;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useMarketBI(
  locations: MapLocationBI[],
  targets: Record<string, TargetEntry>,
  dailyHistoryRaw: DailyHistoryEntry[],
  month: string,
) {
  // Brand summary cards
  const brandSummaries = useMemo<BrandSummary[]>(() => {
    const map = new Map<string, BrandSummary>();
    locations.forEach(loc => {
      const key = loc.brand_id || '__none__';
      const existing = map.get(key) ?? {
        brandId: key,
        brandName: loc.brand_name || 'Unassigned',
        brandLogo: loc.brand_logo_url || '',
        branchCount: 0,
        totalTarget: 0,
        totalActual: 0,
        achievementPct: 0,
        gap: 0,
      };
      existing.branchCount++;
      const t = targets[loc.id];
      const monthlyTarget = Number(t?.monthly) || loc.effective_monthly_target || loc.monthly_target || 0;
      existing.totalTarget += monthlyTarget;
      existing.totalActual += loc.current_sales ?? 0;
      map.set(key, existing);
    });
    // Calculate derived fields
    for (const s of map.values()) {
      s.achievementPct = s.totalTarget > 0 ? (s.totalActual / s.totalTarget) * 100 : 0;
      s.gap = s.totalActual - s.totalTarget;
    }
    return Array.from(map.values()).sort((a, b) => a.brandName.localeCompare(b.brandName));
  }, [locations, targets]);

  // Organization-wide totals
  const orgTotals = useMemo(() => {
    let totalTarget = 0;
    let totalActual = 0;
    locations.forEach(loc => {
      const t = targets[loc.id];
      totalTarget += Number(t?.monthly) || loc.effective_monthly_target || loc.monthly_target || 0;
      totalActual += loc.current_sales ?? 0;
    });
    return {
      totalTarget,
      totalActual,
      achievementPct: totalTarget > 0 ? (totalActual / totalTarget) * 100 : 0,
      gap: totalActual - totalTarget,
    };
  }, [locations, targets]);

  // Daily velocity for the line chart
  const dailyVelocity = useMemo<DailyVelocityPoint[]>(() => {
    if (!month) return [];
    const [y, m] = month.split('-').map(Number);
    const daysInMonth = new Date(y, m, 0).getDate();
    const dailyIdealRate = orgTotals.totalTarget / daysInMonth;

    // Aggregate all locations by day
    const dayMap = new Map<number, number>();
    dailyHistoryRaw.forEach(entry => {
      const d = new Date(entry.entry_date).getDate();
      dayMap.set(d, (dayMap.get(d) || 0) + Number(entry.sales_amount || 0));
    });

    const points: DailyVelocityPoint[] = [];
    let cumActual = 0;
    const today = new Date();
    const currentDay = today.getFullYear() === y && today.getMonth() + 1 === m ? today.getDate() : daysInMonth;

    for (let d = 1; d <= Math.min(currentDay, daysInMonth); d++) {
      const daySales = dayMap.get(d) || 0;
      cumActual += daySales;
      points.push({
        day: d,
        date: `${month}-${String(d).padStart(2, '0')}`,
        totalSales: daySales,
        idealCumulative: Math.round(dailyIdealRate * d),
        actualCumulative: Math.round(cumActual),
      });
    }
    return points;
  }, [dailyHistoryRaw, month, orgTotals.totalTarget]);

  // Projection intelligence
  const projection = useMemo<ProjectionResult>(() => {
    const [y, m] = month.split('-').map(Number);
    const daysInMonth = new Date(y, m, 0).getDate();
    const today = new Date();
    const daysElapsed = today.getFullYear() === y && today.getMonth() + 1 === m
      ? today.getDate()
      : daysInMonth;

    const avgDaily = daysElapsed > 0 ? orgTotals.totalActual / daysElapsed : 0;
    const projectedTotal = Math.round(avgDaily * daysInMonth);
    const projectedPct = orgTotals.totalTarget > 0 ? (projectedTotal / orgTotals.totalTarget) * 100 : 0;
    const projectedGap = projectedTotal - orgTotals.totalTarget;

    return {
      totalTarget: orgTotals.totalTarget,
      totalActual: orgTotals.totalActual,
      projectedTotal,
      projectedPct,
      projectedGap,
      daysElapsed,
      daysInMonth,
    };
  }, [month, orgTotals]);

  // Top/bottom performers
  const leaderboard = useMemo(() => {
    const list: BranchPerformance[] = locations.map(loc => {
      const t = targets[loc.id];
      const target = Number(t?.monthly) || loc.effective_monthly_target || loc.monthly_target || 0;
      const actual = loc.current_sales ?? 0;
      return {
        id: loc.id,
        name: loc.name,
        brandName: loc.brand_name || 'Unassigned',
        target,
        actual,
        achievementPct: target > 0 ? (actual / target) * 100 : 0,
        gap: actual - target,
      };
    });
    const sorted = [...list].sort((a, b) => b.achievementPct - a.achievementPct);
    const top = sorted.filter(b => b.target > 0).slice(0, 5);
    const bottom = sorted.filter(b => b.target > 0).reverse().slice(0, 5);
    return { top, bottom };
  }, [locations, targets]);

  return { brandSummaries, orgTotals, dailyVelocity, projection, leaderboard };
}
